import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import { Analytics } from '@vercel/analytics/react';
import 'react-toastify/dist/ReactToastify.css';

// Import components
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import WidgetList from './pages/WidgetList';
import EmbedWidgetAuth from './pages/EmbedWidgetAuth';
import CreateWidget from './pages/CreateWidget';
import IntegrationsPage from './pages/IntegrationsPage';

// Import pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import AgentsListPage from './pages/AgentsListPage';
import ProfilePage from './pages/ProfilePage';
import AgentCreatePage from './pages/AgentCreatePage';
import AgentDetailsPage from './pages/AgentDetailsPage';
import CallsPage from './pages/CallsPage';
import CreateCallPage from './pages/CreateCallPage';
import CallDetailsPage from './pages/CallDetailsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import WhiteLabelSettingsPage from './pages/WhiteLabelSettingsPage';
import ApiKeysPage from './pages/ApiKeysPage';
import PhoneNumbersPage from './pages/PhoneNumbersPage';
import AdminDashboard from './pages/AdminDashboard';
import InviteAcceptPage from './pages/InviteAcceptPage';

// Import context
import { AuthProvider } from './utils/AuthContext';
import { useAuth } from './utils/AuthContext';

// Create a modern theme for Call Ninja
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea', // Modern purple-blue
      light: '#8b9ef8',
      dark: '#4a5fd8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#764ba2', // Complementary purple
      light: '#9a6bc4',
      dark: '#5a3d7a',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
  },
});

const AppContent = () => {
  const { currentUser } = useAuth();

  return (
    <>
      {currentUser && <Sidebar />}
      <div style={{ marginLeft: currentUser ? '280px' : '0' }}>
        <Container className="py-4">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/agents" element={
              <ProtectedRoute>
                <AgentsListPage />
              </ProtectedRoute>
            } />

            {/* Integrations route */}
            <Route path="/integrations" element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            } />

            {/* Agent routes */}
            <Route path="/agents" element={
              <ProtectedRoute>
                <AgentCreatePage />
              </ProtectedRoute>
            } />
            <Route path="/agents/create" element={
              <ProtectedRoute>
                <AgentCreatePage />
              </ProtectedRoute>
            } />
            <Route path="/agents/:id" element={
              <ProtectedRoute>
                <AgentDetailsPage />
              </ProtectedRoute>
            } />

            {/* Call routes */}
            <Route path="/calls" element={
              <ProtectedRoute>
                <CallsPage />
              </ProtectedRoute>
            } />
            <Route path="/calls/create" element={
              <ProtectedRoute>
                <CreateCallPage />
              </ProtectedRoute>
            } />
            <Route path="/calls/:id" element={
              <ProtectedRoute>
                <CallDetailsPage />
              </ProtectedRoute>
            } />

            {/* Analytics routes */}
            <Route path="/analytics" element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } />

            {/* Phone Numbers routes */}
            <Route path="/phone-numbers" element={
              <ProtectedRoute>
                <PhoneNumbersPage />
              </ProtectedRoute>
            } />

            {/* Organization routes */}
            <Route path="/organization/white-label" element={
              <ProtectedRoute>
                <WhiteLabelSettingsPage />
              </ProtectedRoute>
            } />

            {/* User routes */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            {/* API Keys route */}
            <Route path="/api-keys" element={
              <ProtectedRoute>
                <ApiKeysPage />
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />

            {/* Invitation routes */}
            <Route path="/invite/accept" element={<InviteAcceptPage />} />

            {/* Widget routes */}
            <Route path="/widgets" element={<WidgetList />} />
            <Route path="/embed/auth" element={<EmbedWidgetAuth />} />
            <Route path="/widgets/create" element={<CreateWidget />} />

            {/* Fallback route */}
            <Route path="*" element={<HomePage />} />
          </Routes>
        </Container>
      </div>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Analytics />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
